import { Head, Link, useForm } from '@inertiajs/react'
import type { FormEvent } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface Tune {
  id: number
  name: string
  tune_type: string | null
  musical_key: string | null
  composer: string | null
}

interface Props {
  tune: Tune
  errors?: Record<string, string | string[]>
}

const NONE = 'none'
const TUNE_TYPES = [
  'reel',
  'jig',
  'slip jig',
  'hornpipe',
  'polka',
  'slide',
  'waltz',
  'barndance',
  'strathspey',
  'three-two',
  'mazurka',
  'march',
]

export default function Edit({ tune, errors }: Props) {
  const form = useForm({
    thesession_url: '',
    tune: {
      name: tune.name,
      tune_type: tune.tune_type ?? '',
      musical_key: tune.musical_key ?? '',
      composer: tune.composer ?? '',
    },
  })

  const nameError = errors?.name
  const showNameError = Array.isArray(nameError) ? nameError.join(', ') : nameError

  function setField(field: keyof typeof form.data.tune, value: string) {
    form.setData('tune', { ...form.data.tune, [field]: value })
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    form.patch(`/tunes/${tune.id}`)
  }

  return (
    <>
      <Head title={`Edit ${tune.name}`} />

      <div className="mb-6 flex items-center justify-between">
        <div>
          <Link
            href={`/tunes/${tune.id}`}
            className="text-sm text-muted-foreground hover:underline"
          >
            ← {tune.name}
          </Link>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">Edit tune</h1>
        </div>
        <Button variant="ghost" asChild>
          <Link href={`/tunes/${tune.id}`}>Cancel</Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="thesession_url">thesession.org URL</Label>
              <Input
                id="thesession_url"
                placeholder="https://thesession.org/tunes/63"
                value={form.data.thesession_url}
                onChange={(e) => form.setData('thesession_url', e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Paste a link to fill in any blank fields below. Won't overwrite what you've entered.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={form.data.tune.name}
                onChange={(e) => setField('name', e.target.value)}
              />
              {showNameError && <p className="text-xs text-destructive">{showNameError}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="tune_type">Type</Label>
              <Select
                value={form.data.tune.tune_type || NONE}
                onValueChange={(v) => setField('tune_type', v === NONE ? '' : v)}
              >
                <SelectTrigger id="tune_type" className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>—</SelectItem>
                  {TUNE_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="musical_key">Key</Label>
              <Input
                id="musical_key"
                placeholder="Edorian"
                value={form.data.tune.musical_key}
                onChange={(e) => setField('musical_key', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="composer">Composer</Label>
              <Input
                id="composer"
                placeholder="Trad / Paddy Fahey…"
                value={form.data.tune.composer}
                onChange={(e) => setField('composer', e.target.value)}
              />
            </div>

            <Button type="submit" disabled={form.processing}>
              Save
            </Button>
          </form>
        </CardContent>
      </Card>
    </>
  )
}
